import actionTypes from './actionTypes';
import UserSource from '../../sources/user';
import { addNotification } from './notification';
import { fetchCurrentTenant } from './currentTenant';
import { navigate } from '@reach/router';
import TenantSource from '../../sources/tenant';
import jwtDecode from 'jwt-decode';

function getCookie(cname) {
    var name = cname + "=";
    var decodedCookie = decodeURIComponent(document.cookie);
    var ca = decodedCookie.split(';');
    for (var i = 0; i < ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) == ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
}

function setCookie(cname, cvalue, exdays) {
    const d = new Date();
    d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
    let expires = "expires="+d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

/*
Example user state:
{
    authenticationToken: string
    tenantId: string,
    tenantName: string,
    username: string,
    orgId: string,
    orgName: string,
    roleName: string,
    createdAt: string,
    email: string,
    firstName: string,
    id: string,
    lastName: string,
    organizations: [
      {
        id: string,
        name: string
      }
    ],
    password: null,
    tenants: [],
    verified: bool
}
*/

export const setCurrentUser = (partialUser) => (dispatch, getState) => {
    const { currentUser } = getState();
    const updatedUser = { ...currentUser, ...partialUser };

    dispatch({
        type: actionTypes.CURRENT_USER_SET,
        payload: updatedUser,
    });
};

export const signOut = () => (dispatch) => {
    setCookie('boomi-flow-token', '', -1);

    dispatch({
        type: actionTypes.CURRENT_USER_REMOVE,
    });

    navigate('/');
};

export const fetchCurrentUser = (authenticationToken) => async (dispatch) => {
    try {
        const user = await UserSource.get(authenticationToken);
        dispatch(setCurrentUser(user));
    } catch (err) {
        addNotification({
            type: 'error',
            message: err.message,
            isPersistent: true,
        });
    }
};

export const initializeCurrentUser = () => async (dispatch) => {
    // Clean up legacy 'flowUser' removed in FLOW-3303
    window.sessionStorage.removeItem('flowUser'); 

    let token = null;

    const urlParams = new URLSearchParams(window.location.search);

    // If we're loading the tooling from an SSO redirect, perform a "login" from the provided token
    if (urlParams.has('token')) {
        token = urlParams.get('token');
        // Remove the token from the browser address, otherwise people will accidentally copy/paste their credentials
        window.history.replaceState({}, document.title, window.location.pathname);
    }
    else if (getCookie('boomi-flow-token')) {
        token = getCookie('boomi-flow-token');
    }

    if (token) {
        const tenantIdFromUrl = window.location.pathname.split('/').filter(p => !!p)[0];
        let claims = jwtDecode(token);

        // If the tenant ID stored in the cookie is not the same as the tenant ID
        // specified in the URL, then the tenant ID in the store should be set to null.
        // This is because, the specified tenant ID may be invalid/unauthorized, in which
        // case it should only be deterimined once the user has successfully been authorized
        // into the specified tenant.
        // This prevents us sending the wrong tenant ID to Pendo during initialization.
        const tenantId = !tenantIdFromUrl ? claims.tenant : claims.tenant === tenantIdFromUrl ? claims.tenant : null;

        const user = {
            authenticationToken: token,
            tenantId,
            tenant: tenantId,
            email: claims.email,
            tenantName: tenantId ? claims.tenant_name : null,
            isSso: claims.is_sso,
            roleName: claims.role_name,
        };

        dispatch(setCurrentUser(user));

        if (claims.tenant) {
            dispatch(fetchCurrentTenant(token, claims.tenant));
        }

        if ((!claims.tenant && tenantIdFromUrl) || (claims.tenant !== tenantIdFromUrl && tenantIdFromUrl)) {

            try {
                token = await TenantSource.switch(tenantIdFromUrl, token);
                claims = jwtDecode(token);

                dispatch(setCurrentUser({
                    tenantId: claims.tenant,
                    tenant: claims.tenant,
                    tenantName: claims.tenantName,
                    roleName: claims.role_name,
                }));
    
                dispatch(fetchCurrentTenant(token, claims.tenant));
            } catch ({ message }) {

                // If we cannot switch to the tenant specified in the url
                // then we need to reset the details in the store.
                // This will trigger the routing mechanism to display the tenant selector
                // once we have navigated back to the root domain.
                dispatch(setCurrentUser({
                    tenantId: null,
                    tenant: null,
                    tenantName: null,
                    roleName: '',
                }));

                navigate('/');

                dispatch(addNotification({
                    type: 'error',
                    message,
                    isPersistent: true,
                }));
            }
        }       

        const event = {
            beforeSend: function (xhr) {
                if (xhr) {
                    xhr.setRequestHeader('X-ManyWho-Effective-Tenant', claims.tenant);
                    xhr.setRequestHeader('X-ManyWho-Effective-User', claims.sub);
                }
            },
        };

        // Add the interceptor to all used runtime API calls
        manywho.settings.initialize({}, {
            fileData: event,
            flowOut: event,
            getFlowByName: event,
            initialization: event,
            invoke: event,
            join: event,
            navigation: event,
            objectData: event,
            ping: event,
        });

        // Fetch and update the user information stored in state and session storage
        dispatch(fetchCurrentUser(token));
    }
};
